import { useStore } from "@/stores/useStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { TimeInContainerMetric } from "@/types/v2";
import { calculateTimeInContainer } from "@/lib/containerMetricsMath";
import { useDeltaDisplayMode } from "@/components/Common/DeltaDisplay";
import { useTickerStore } from "@/stores/useTickerStore";

export const useContainerMetrics = (containerId: string) => {
  const metrics = useStore((s) => s.timeInContainerMetrics);
  const shop = useStore((s) => s.shop);
  const { ticker } = useTickerStore();
  const container = shop?.containers.find((c) => c.id === containerId);
  const cards = useMemo(() => container?.tags ?? [], [container]);
  const [weekAverage, setWeekAverage] = useState(0);
  const [averageTimeInContainer, setAverageTimeInContainer] = useState(0);
  const { displayMode: deltaDisplayMode } = useDeltaDisplayMode();

  const todayMetrics = metrics
    .find((metric) => metric.id === format(new Date(), "yyyy-MM-dd"))
    ?.timeInContainer.find((met) => met.containerId === containerId);

  const currentMetrics = useMemo(
    () =>
      metrics
        .filter((metric) => metric.id === format(new Date(), "yyyy-MM-dd"))
        .map(
          (metric): TimeInContainerMetric => ({
            ...metric,
            timeInContainer: metric.timeInContainer.filter(
              (met) => met.containerId === containerId
            ),
          })
        ),
    [metrics, containerId]
  );

  const weekMetrics = useMemo(
    () =>
      metrics
        .filter((metric) => metric.id !== format(new Date(), "yyyy-MM-dd"))
        .map(
          (metric): TimeInContainerMetric => ({
            ...metric,
            timeInContainer: metric.timeInContainer.filter(
              (met) => met.containerId === containerId
            ),
          })
        ),
    [metrics, containerId]
  );

  const getWeeklyTime = useCallback(() => {
    const averages = weekMetrics.map((metric) => {
      if (!metric) return 0;
      const time = calculateTimeInContainer({
        metric,
        workingHours: shop?.shopConfig?.workingHours,
      });
      return isNaN(time) ? 0 : time;
    });

    setWeekAverage(
      averages.reduce((acc, curr) => acc + curr, 0) /
        Math.max(averages.length, 1)
    );
  }, [weekMetrics, shop?.shopConfig?.workingHours]);

  const getCurrentTime = useCallback(() => {
    const averages = currentMetrics.map((metric) => {
      if (!metric) return 0;
      const time = calculateTimeInContainer({
        metric,
        cards,
        workingHours: shop?.shopConfig?.workingHours,
      });

      return isNaN(time) ? 0 : time;
    });
    // Unique case when there is no metric yet
    if (averages.length === 0 && cards.length > 0) {
      const time = calculateTimeInContainer({
        cards,
        workingHours: shop?.shopConfig?.workingHours,
      });
      setAverageTimeInContainer(time);
      return;
    }
    setAverageTimeInContainer(
      averages.reduce((acc, curr) => acc + curr, 0) /
        Math.max(averages.length, 1)
    );
  }, [currentMetrics, cards, shop?.shopConfig?.workingHours]);

  useEffect(() => {
    getWeeklyTime();
    getCurrentTime();
  }, [ticker, todayMetrics, cards, getWeeklyTime, getCurrentTime]);

  return {
    averageTimeInContainer,
    weekAverage,
    deltaDisplayMode,
  };
};
